import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Globe, Plus, Trash, ShieldCheck, Copy } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function DomainMapping() {
  var [domains, setDomains] = useState([]);
  var [loading, setLoading] = useState(true);
  var [dialogOpen, setDialogOpen] = useState(false);
  var [verifyInfo, setVerifyInfo] = useState(null);
  var [form, setForm] = useState({ domain: '', brand_name: '', primary_color: '#3B82F6', logo_url: '' });

  var fetchDomains = function () {
    api.get('/domains/').then(function (r) { setDomains(r.data); })
      .catch(console.error).finally(function () { setLoading(false); });
  };

  useEffect(function () { fetchDomains(); }, []);

  var addDomain = async function (e) {
    e.preventDefault();
    try {
      await api.post('/domains/', form);
      toast.success('Domain added');
      setDialogOpen(false);
      setForm({ domain: '', brand_name: '', primary_color: '#3B82F6', logo_url: '' });
      fetchDomains();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add domain'); }
  };

  var deleteDomain = async function (id) {
    try {
      await api.delete('/domains/' + id);
      toast.success('Domain removed');
      fetchDomains();
    } catch (err) { toast.error('Failed to delete'); }
  };

  var showVerify = async function (id) {
    try {
      var resp = await api.get('/domains/' + id + '/verify');
      setVerifyInfo(resp.data);
    } catch (err) { toast.error('Failed to get verification info'); }
  };

  var copyText = async function (text) {
    try { await navigator.clipboard.writeText(text); toast.success('Copied'); }
    catch (_e) { toast.success('Copied'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="domain-mapping">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight flex items-center gap-3">
            <Globe size={32} className="text-blue-400" weight="duotone" />
            Custom Domain Mapping
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Map custom domains to white-label configurations</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-domain-btn" className="bg-blue-600 hover:bg-blue-700 text-white gap-2"><Plus size={16} /> Add Domain</Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
            <DialogHeader><DialogTitle className="font-heading text-white">Add Custom Domain</DialogTitle></DialogHeader>
            <form onSubmit={addDomain} className="space-y-4">
              <div><Label className="text-zinc-300">Domain</Label><Input data-testid="domain-input" value={form.domain} onChange={function (e) { setForm(function (p) { return Object.assign({}, p, { domain: e.target.value }); }); }} required placeholder="app.yourbrand.com" className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100" /></div>
              <div><Label className="text-zinc-300">Brand Name</Label><Input data-testid="domain-brand" value={form.brand_name} onChange={function (e) { setForm(function (p) { return Object.assign({}, p, { brand_name: e.target.value }); }); }} required className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100" /></div>
              <div>
                <Label className="text-zinc-300">Primary Color</Label>
                <div className="flex gap-3 mt-1">
                  <input type="color" value={form.primary_color} onChange={function (e) { setForm(function (p) { return Object.assign({}, p, { primary_color: e.target.value }); }); }} className="w-12 h-10 rounded border border-zinc-800 bg-zinc-950 cursor-pointer" />
                  <Input value={form.primary_color} onChange={function (e) { setForm(function (p) { return Object.assign({}, p, { primary_color: e.target.value }); }); }} className="flex-1 bg-zinc-950 border-zinc-800 text-zinc-100 font-mono" />
                </div>
              </div>
              <div><Label className="text-zinc-300">Logo URL</Label><Input value={form.logo_url} onChange={function (e) { setForm(function (p) { return Object.assign({}, p, { logo_url: e.target.value }); }); }} placeholder="https://..." className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100" /></div>
              <Button data-testid="submit-domain" type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white">Add Domain</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-3"><CardTitle className="font-heading text-lg text-white">Mapped Domains</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800 hover:bg-transparent">
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Domain</TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Brand</TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Color</TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Verified</TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Status</TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {domains.map(function (d, i) {
                return (
                  <TableRow key={i} className="border-zinc-800/50" data-testid={'domain-row-' + i}>
                    <TableCell className="text-blue-400 font-mono text-sm">{d.domain}</TableCell>
                    <TableCell className="text-zinc-200">{d.brand_name}</TableCell>
                    <TableCell><div className="flex items-center gap-2"><div className="w-4 h-4 rounded" style={{ backgroundColor: d.primary_color }} /><span className="text-zinc-400 text-xs font-mono">{d.primary_color}</span></div></TableCell>
                    <TableCell>
                      <Badge className={d.verified ? 'bg-emerald-500/10 text-emerald-400 text-[10px]' : 'bg-amber-500/10 text-amber-400 text-[10px]'}>
                        {d.verified ? 'Verified' : 'Pending'}
                      </Badge>
                    </TableCell>
                    <TableCell><Badge className="bg-zinc-700/30 text-zinc-400 text-[10px]">{d.status}</Badge></TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={function () { showVerify(d.mapping_id); }} data-testid={'verify-domain-' + i} className="text-zinc-400 hover:text-blue-400 text-xs gap-1"><ShieldCheck size={14} /> Verify</Button>
                        <Button variant="ghost" size="sm" onClick={function () { deleteDomain(d.mapping_id); }} data-testid={'delete-domain-' + i} className="text-zinc-400 hover:text-red-400"><Trash size={14} /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              {domains.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-zinc-500 py-8">No custom domains configured.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {verifyInfo && (
        <Card className="bg-zinc-900 border-zinc-800 border-l-4 border-l-blue-500">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-base text-white flex items-center gap-2"><ShieldCheck size={18} className="text-blue-400" /> Domain Verification: {verifyInfo.domain}</CardTitle>
              <Button variant="ghost" size="sm" onClick={function () { setVerifyInfo(null); }} className="text-zinc-400">Close</Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-zinc-300">{verifyInfo.instructions}</p>
            <div className="bg-zinc-950 rounded-lg p-3 border border-zinc-800 flex items-center justify-between">
              <code className="text-sm text-blue-400 font-mono">dw-verify={verifyInfo.verification_token}</code>
              <Button variant="ghost" size="sm" onClick={function () { copyText('dw-verify=' + verifyInfo.verification_token); }} className="text-zinc-400 hover:text-blue-400"><Copy size={14} /></Button>
            </div>
            <Badge className={verifyInfo.verified ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'}>
              {verifyInfo.verified ? 'Domain Verified' : 'Awaiting DNS Verification'}
            </Badge>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
